import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ShopProvider, useShop } from "@/store";
import { Marquee, Header, Footer, Newsletter, CookieBanner } from "@/components/Chrome";
import DrawerHost from "@/components/Drawers";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Story from "@/pages/Story";
import Artisans from "@/pages/Artisans";
import Impact from "@/pages/Impact";
import { Journal, Article } from "@/pages/Journal";
import { Wholesale, Gifting, Press, Careers, Investors, Help, Contact, Account, Checkout, NotFound } from "@/pages/Misc";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  const { lenisRef, setDrawer } = useShop();
  useEffect(() => {
    setDrawer(null);
    const lenis = lenisRef.current;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        if (el.tagName === "DETAILS") el.open = true;
        const timer = setTimeout(() => {
          if (lenis) lenis.scrollTo(el, { offset: -100 });
          else el.scrollIntoView();
        }, 150);
        return () => clearTimeout(timer);
      }
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash, lenisRef, setDrawer]);
  return null;
}

function Shell() {
  const { lenisRef } = useShop();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [lenisRef]);

  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <Marquee />
      <Header />
      <ScrollManager />
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<Product />} />
          <Route path="/story" element={<Story />} />
          <Route path="/artisans" element={<Artisans />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/verify" element={<Impact />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:id" element={<Article />} />
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/gifting" element={<Gifting />} />
          <Route path="/press" element={<Press />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/investors" element={<Investors />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/account" element={<Account />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Newsletter />
      <Footer />
      <DrawerHost />
      <CookieBanner />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "var(--ink)",
            color: "var(--cream)",
            border: "1px solid var(--line)",
            borderRadius: 2,
            fontSize: 12,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            fontFamily: "var(--body)",
          },
        }}
      />
    </>
  );
}

export default function App() {
  return (
    <ShopProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ShopProvider>
  );
}
